import React, { useMemo } from "react";
import type {
	APAADMINBaseModelsNetworkStatusTargetView,
	APAADMINBaseModelsNetworkStatusPoint,
	APAADMINBaseModelsNetworkStatusLatestSample
} from "../../API/Models";
import { formatRelativeTime } from "../../Helper/dates";
import { severityStyle } from "./dashboardShared";

const HEALTHY_THRESHOLD = 350;
const DEGRADED_THRESHOLD = 800;
const MAX_POINTS = 16;

const pickAverageValue = (point?: APAADMINBaseModelsNetworkStatusPoint | null) => {
	if (!point) return 0;
	const avg = point.averagePingMs;
	if (typeof avg === "number" && !Number.isNaN(avg) && avg > 0) return avg;
	return point.pingMs ?? 0;
};

/** Map a latest network sample to a severity (0 down … 3 unknown) shared with the command bar. */
export const targetSeverity = (latest?: APAADMINBaseModelsNetworkStatusLatestSample | null): number => {
	if (!latest || !latest.sampledAt) return 3;
	if (!latest.isSuccess) return 0;
	if (latest.pingMs <= HEALTHY_THRESHOLD) return 2;
	return 1; // degraded / slow
};

const severityText = (severity: number, latest?: APAADMINBaseModelsNetworkStatusLatestSample | null): string => {
	if (severity === 3) return "Aucune donnée";
	if (severity === 0) return "Indisponible";
	if (latest && latest.pingMs <= HEALTHY_THRESHOLD) return "Optimal";
	if (latest && latest.pingMs <= DEGRADED_THRESHOLD) return "Ralenti";
	return "Lent";
};

const formatEndpointLabel = (url?: string | null) => {
	if (!url) return "—";
	try {
		return new URL(url).host || url;
	} catch {
		return url;
	}
};

const MiniLatencyBars: React.FC<{ data: number[]; barClass: string }> = ({ data, barClass }) => {
	if (!data || data.length === 0) return <span className="text-[11px] text-slate-400">—</span>;
	const maxValue = Math.max(...data, 1);
	return (
		<div className="flex h-9 w-24 items-end gap-0.5">
			{data.map((value, index) => (
				<span
					key={`lat-${index}`}
					className={`flex-1 rounded-sm ${barClass}`}
					style={{ height: `${Math.max((value / maxValue) * 100, 8)}%` }}
				/>
			))}
		</div>
	);
};

const barClassForSeverity = (severity: number): string => {
	switch (severity) {
		case 0:
			return "bg-rose-400/70";
		case 1:
			return "bg-amber-400/70";
		case 2:
			return "bg-emerald-400/70";
		default:
			return "bg-slate-300/70";
	}
};

export interface NetworkTelemetryProps {
	targets: APAADMINBaseModelsNetworkStatusTargetView[];
	generatedAt?: string | null;
	loading: boolean;
	error?: string | null;
}

/** Per-endpoint latency cards (the 7 monitored targets), sorted worst-first. */
export const NetworkTelemetry: React.FC<NetworkTelemetryProps> = ({ targets, generatedAt, loading, error }) => {
	const rows = useMemo(() => {
		return (targets ?? [])
			.map((target) => {
				const points = target.points ?? [];
				const recent = points.slice(-MAX_POINTS);
				const sparkline = recent.map((point) => Math.max(0, Math.round(pickAverageValue(point))));
				const latest = target.latest;
				const severity = targetSeverity(latest);
				const style = severityStyle(severity);
				return {
					id: target.target ?? target.label ?? target.url ?? Math.random().toString(36).slice(2),
					label: target.label ?? target.target ?? "Endpoint",
					url: target.url,
					severity,
					style,
					sparkline,
					latestValue: latest && latest.isSuccess ? `${Math.round(latest.pingMs ?? 0)} ms` : latest ? "Down" : "—",
					averageLabel:
						points.length > 0 ? `${Math.round(pickAverageValue(points[points.length - 1]))} ms moy.` : "En attente",
					relative: latest?.sampledAt ? formatRelativeTime(latest.sampledAt) : "Aucun relevé",
					text: severityText(severity, latest)
				};
			})
			.sort((a, b) => a.severity - b.severity);
	}, [targets]);

	return (
		<section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
			<div className="flex flex-wrap items-center gap-2 sm:gap-3">
				<div>
					<p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Infrastructure</p>
					<p className="text-xs text-slate-400">
						{generatedAt ? `Mis à jour ${formatRelativeTime(generatedAt)}` : "En attente du premier scan"}
					</p>
				</div>
				<a
					href="/network-status"
					className="ml-auto inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
				>
					Rapport complet
				</a>
			</div>
			<div className="mt-4">
				{loading ? (
					<div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{Array.from({ length: 4 }).map((_, i) => (
							<div key={`net-skel-${i}`} className="h-28 animate-pulse rounded-xl border border-slate-100 bg-slate-50" />
						))}
					</div>
				) : error ? (
					<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
				) : rows.length === 0 ? (
					<div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
						Les données réseau apparaîtront après le premier cycle du scanner.
					</div>
				) : (
					<div className="grid gap-3 grid-cols-1 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
						{rows.map((row) => (
							<div key={row.id} className={`rounded-xl border p-3 sm:p-4 ${row.style.chip}`}>
								<div className="flex items-center gap-2">
									<span className={`inline-flex h-2.5 w-2.5 rounded-full ${row.style.dot}`} />
									<p className="truncate text-sm font-semibold text-slate-900">{row.label}</p>
								</div>
								<div className="mt-2 flex items-end justify-between gap-2">
									<div>
										<p className="text-lg font-bold tabular-nums text-slate-900">{row.latestValue}</p>
										<p className="text-[11px] text-slate-500">{row.averageLabel}</p>
									</div>
									<MiniLatencyBars data={row.sparkline} barClass={barClassForSeverity(row.severity)} />
								</div>
								<p className="mt-2 text-xs text-slate-500">
									{row.text} · {row.relative}
								</p>
								{row.url && (
									<a
										className="mt-1 block truncate text-[11px] font-semibold text-slate-500 underline-offset-2 hover:text-slate-800"
										href={row.url}
										target="_blank"
										rel="noreferrer"
									>
										{formatEndpointLabel(row.url)}
									</a>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</section>
	);
};

export default NetworkTelemetry;
